import { core } from '@tock/coretypes';
import { find, includes, isEmpty, reduce, take, uniq } from 'es-toolkit/compat';

interface LocationDetails {
  city?: string | null;
  state?: string | null;
  country?: string | null;
}

function isNotNilOrEmpty(str?: string | null): str is string {
  return !((str == null) || isEmpty(str.trim()));
}

export function displayLocation({ city, state, country }: LocationDetails): string {
  const returnArr: string[] = [];
  if (isNotNilOrEmpty(city)) {
    returnArr.push(city);
  }
  if (isNotNilOrEmpty(state)) {
    returnArr.push(state);
  }
  if (isNotNilOrEmpty(country)) {
    returnArr.push(country);
  }

  return take(uniq(returnArr), 2).join(', ');
}

export function locationIsOutsideUS(country: string) {
  switch (country) {
    case undefined:
    case 'US':
    case 'USA':
    case 'United States': {
      return false;
    }
    default: {
      return true;
    }
  }
}

export function displayAddress(location: core.ILocation): string {
  const displayOrder = [
    location.name,
    location.address,
    location.address2,
    location.city,
    location.state,
    location.country,
    location.zipCode,
  ];
  return reduce(
    displayOrder,
    (addressString: string, nextAddressPiece: string | null | undefined) => {
      return addressString + withOptComma(nextAddressPiece, !isEmpty(addressString));
    },
    ''
  );
}

function withOptComma(str: string | null | undefined, displayOptComma: boolean): string {
  if ((str == null) || isEmpty(str)) {
    return '';
  }
  return displayOptComma ? `, ${str}` : str;
}

export function placeResultToLocation(
  addressComponents: google.maps.GeocoderAddressComponent[],
  googlePlacesId?: string
): core.ILocation {
  function addressComponent(field: string) {
    return find(addressComponents, (comp) => includes(comp.types, field))?.short_name ?? '';
  }

  function longAddressComponent(field: string) {
    return find(addressComponents, (comp) => includes(comp.types, field))?.long_name ?? '';
  }

  const country = addressComponent('country');
  const streetNumber = addressComponent('street_number');
  const streetName = addressComponent('route');
  const city =
    longAddressComponent('locality') ||
    longAddressComponent('postal_town') ||
    longAddressComponent('sublocality') ||
    longAddressComponent('administrative_area_level_3') ||
    longAddressComponent('neighborhood');
  const state =
    addressComponent('administrative_area_level_1') ||
    addressComponent('administrative_area_level_2');
  const zipCode = addressComponent('postal_code');

  return {
    address: `${streetNumber} ${streetName}`,
    city,
    state,
    country,
    zipCode,
    googlePlacesId,
  };
}

// TODO: Account for jursidictions with different prefixes (e.g., "Commonwealth of MA/PA/VA/KY")
export function getOfficialUsStateName(state?: string | null): string {
  if (!state) {
    return 'your state';
  }

  switch (state) {
    case 'DC':
      return 'Washington, D.C.';
    default:
      return `the State of ${state}`;
  }
}
